import React from 'react';
import { motion } from 'framer-motion';
import {
  SiUnity, SiDotnet, SiCplusplus, SiHtml5, SiCss,
  SiJavascript, SiReact, SiBootstrap, SiGit, SiSteam,
  SiBlender, SiTrello, SiXcode, SiSwift,
  SiOpenai, SiLangchain,
} from 'react-icons/si';
import { HiPuzzle, HiDesktopComputer, HiWifi, HiSparkles } from 'react-icons/hi';
import { VscCode } from 'react-icons/vsc';
import { useLanguage } from '../i18n/LanguageContext';
import './Skills.css';

const skillCategories = [
  {
    title: 'Game Development',
    icon: <HiPuzzle />,
    skills: [
      { name: 'Unity', icon: <SiUnity />, level: 95 },
      { name: 'C#', icon: <SiDotnet />, level: 95 },
      { name: 'Netcode for GameObjects', icon: <HiWifi />, level: 85 },
      { name: 'Steamworks.NET', icon: <SiSteam />, level: 80 },
      { name: 'Game Design', icon: <HiPuzzle />, level: 87 },
      { name: 'C++', icon: <SiCplusplus />, level: 70 },
    ],
  },
  {
    title: 'Web Development',
    icon: <HiDesktopComputer />,
    skills: [
      { name: 'HTML5', icon: <SiHtml5 />, level: 90 },
      { name: 'CSS3', icon: <SiCss />, level: 87 },
      { name: 'JavaScript', icon: <SiJavascript />, level: 83 },
      { name: 'React', icon: <SiReact />, level: 80 },
      { name: 'Bootstrap', icon: <SiBootstrap />, level: 85 },
    ],
  },
  {
    title: 'Tools & Other',
    icon: <SiGit />,
    skills: [
      { name: 'Git', icon: <SiGit />, level: 90 },
      { name: 'Visual Studio', icon: <VscCode />, level: 93 },
      { name: 'Xcode', icon: <SiXcode />, level: 77 },
      { name: 'Swift', icon: <SiSwift />, level: 73 },
      { name: 'Trello', icon: <SiTrello />, level: 70 },
      { name: 'Blender', icon: <SiBlender />, level: 70 },
    ],
  },
  {
    title: 'AI & Development',
    icon: <HiSparkles />,
    skills: [
      { name: 'AI Coding Agents', icon: <SiOpenai />, level: 90 },
      { name: 'Prompt Engineering', icon: <SiLangchain />, level: 88 },
      { name: 'AI Agents', icon: <HiSparkles />, level: 85 },
      { name: 'Agentic AI', icon: <SiOpenai />, level: 82 },
    ],
  },
];

const Skills = () => {
  const { t } = useLanguage();

  return (
    <section className="skills section" id="skills">
      <div className="container">
        <motion.div
          className="skills__header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">{t('skills.label')}</span>
          <h2 className="section-title">
            {t('skills.titleStart')}<span className="gradient-text">{t('skills.titleHighlight')}</span>
          </h2>
          <p className="section-subtitle">{t('skills.subtitle')}</p>
        </motion.div>

        <div className="skills__grid">
          {skillCategories.map((category, i) => (
            <motion.div
              key={i}
              className="skills__category"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: 0.1 * i + 0.2 }}
            >
              <div className="skills__category-header">
                <span className="skills__category-icon">{category.icon}</span>
                <h3 className="skills__category-title">{category.title}</h3>
              </div>

              <div className="skills__list">
                {category.skills.map((skill, j) => (
                  <motion.div
                    key={j}
                    className="skills__item"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 * j + 0.3 + 0.1 * i }}
                    whileHover={{ x: 4 }}
                  >
                    <div className="skills__item-info">
                      <span className="skills__item-icon">{skill.icon}</span>
                      <span className="skills__item-name">{skill.name}</span>
                    </div>
                    <div className="skills__bar">
                      <motion.div
                        className="skills__bar-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.1 * j + 0.5 + 0.1 * i, ease: [0.4, 0, 0.2, 1] }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
